import { Text } from '@mantine/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { DisplayComponentBaseView } from '../../../../../../../react-commons/views/DisplayComponentBaseView';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultToolResource,
    ToolMutationResourceSchema,
    ToolPropertyValidation,
    useToolActions,
} from '../../../../../resource/resources/artifacts/tool/toolResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { ResourceValidationTitleBar } from '../../../../reusable-components/validation/ResourceValidationTitleBar';
import { OverviewComponentPropertyValidationInformation } from '../../../../util/overviewUtils';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { ToolContent } from './ToolContent';

const ToolOverviewContent = ({ id, displayedInOverview }: { id: string; displayedInOverview: boolean }) => {
    const readOnly = useSelector(selectReadOnly);

    const toolActions = useToolActions();
    const toolQuery = toolActions.useGetResource(id);
    const updateTool = toolActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: ToolMutationResourceSchema,
        defaultValues: defaultToolResource,
        submitHandler: async (data) => {
            await updateTool.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: toolQuery.data,
    });

    return (
        <QueryBaseComponent name="Tool" query={toolQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <ToolContent
                    displayedInOverview={displayedInOverview}
                    form={form}
                    formNames={formNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const ToolOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const toolActions = useToolActions();

    const parametersQuery = toolActions.useGetAllResources(parentId);

    const resourceType = ResourceType.TOOL;

    return (
        <QueryBaseComponent name="Tools" query={parametersQuery}>
            <SingleParentedOverviewComponent
                label={"Tools"}
                resourceQuery={parametersQuery}
                content={(id, displayedInOverview) => (
                    <ToolOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: ToolPropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
